import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import { useServerFn } from "@tanstack/react-start";
import { analyzeScan, submitScanLead } from "@/lib/scan.functions";
import { StickyHeader } from "@/components/site/StickyHeader";
import { Footer } from "@/components/site/Footer";

export const Route = createFileRoute("/scan")({
  head: () => ({
    meta: [
      { title: "Advisor Visibility Scan — Katie Fernands" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ScanPage,
});

const REFERRAL_SOURCES = [
  "Existing clients",
  "CPA",
  "Lawyers",
  "Accountants",
  "Real estate agents",
  "Tax attorneys",
  "Peer advisors",
  "None currently",
];

const REFERRAL_ACTIVITIES = [
  "Local chamber of commerce",
  "Golf",
  "Pickleball",
  "Church",
  "Client events",
  "LinkedIn outreach",
  "Email newsletters",
  "CPA / attorney partnerships",
  "Nothing structured",
];

const RUBRIC = [
  { key: "brand_clarity", label: "Brand Clarity" },
  { key: "positioning_distinctiveness", label: "Positioning Distinctiveness" },
  { key: "audience_specificity", label: "Audience Specificity" },
  { key: "trust_signals", label: "Trust Signals" },
  { key: "referral_readiness", label: "Referral Readiness" },
  { key: "cta_clarity", label: "Call-to-Action Clarity" },
  { key: "social_media_alignment", label: "Social Media Alignment" },
] as const;

type RubricKey = (typeof RUBRIC)[number]["key"];

interface Analysis {
  brand_clarity: { score: number; rationale: string };
  positioning_distinctiveness: { score: number; rationale: string };
  audience_specificity: { score: number; rationale: string };
  trust_signals: { score: number; rationale: string };
  referral_readiness: { score: number; rationale: string };
  cta_clarity: { score: number; rationale: string };
  social_media_alignment: { score: number; rationale: string };
  visibility_gaps: { observations: string };
  immediate_actions: Array<{ priority: number; action: string; why: string }>;
}

const gateSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(200),
  email: z.string().trim().email("Enter a valid email").max(320),
});

function scoreColor(score: number): string {
  if (score <= 1) return "bg-red-600";
  if (score === 2) return "bg-orange-500";
  if (score === 3) return "bg-yellow-500";
  if (score === 4) return "bg-green-600";
  return "bg-green-800";
}

function ScanPage() {
  const [unlocked, setUnlocked] = useState<{ name: string; email: string } | null>(null);

  return (
    <div className="bg-background text-foreground min-h-screen flex flex-col">
      <StickyHeader />
      <main className="flex-1 pt-32 pb-24">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          {!unlocked ? (
            <Gate onUnlock={(lead) => setUnlocked(lead)} />
          ) : (
            <Tool lead={unlocked} />
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Gate({ onUnlock }: { onUnlock: (lead: { name: string; email: string }) => void }) {
  const submitLead = useServerFn(submitScanLead);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const parsed = gateSchema.safeParse({ name, email });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Invalid input");
      return;
    }
    setSubmitting(true);
    try {
      await submitLead({ data: parsed.data });
    } catch {
      setSubmitting(false);
      setError("Something went wrong. Please try again.");
      return;
    }
    setSubmitting(false);
    onUnlock(parsed.data);
  }

  return (
    <div className="mx-auto max-w-lg">
      <p className="text-xs uppercase tracking-[0.24em] text-primary mb-3">
        Advisor Visibility Scan
      </p>
      <h1 className="text-3xl md:text-4xl font-serif tracking-tight">
        Enter your name and email to access the Advisor Visibility Scan.
      </h1>
      <form onSubmit={handleSubmit} className="mt-8 space-y-4">
        <input
          type="text"
          placeholder="Your name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full rounded-full border border-border/60 bg-background px-5 py-3 text-sm focus:border-primary focus:outline-none"
        />
        <input
          type="email"
          placeholder="you@firm.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full rounded-full border border-border/60 bg-background px-5 py-3 text-sm focus:border-primary focus:outline-none"
        />
        {error && <p className="text-sm text-destructive">{error}</p>}
        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded-full bg-primary text-primary-foreground px-6 py-3 text-sm font-medium hover:opacity-90 disabled:opacity-50"
        >
          {submitting ? "Unlocking…" : "Access the Scan"}
        </button>
      </form>
    </div>
  );
}

interface FormState {
  brand_name: string;
  url: string;
  social_url: string;
  warm_intros: string;
  referral_sources: string[];
  referral_sources_other: string;
  referral_activities: string[];
  referral_activities_other: string;
  referral_outreach: string;
  self_description: string;
  testimonial_status: string;
  growth_frustration: string;
}

const initialForm: FormState = {
  brand_name: "",
  url: "",
  social_url: "",
  warm_intros: "",
  referral_sources: [],
  referral_sources_other: "",
  referral_activities: [],
  referral_activities_other: "",
  referral_outreach: "",
  self_description: "",
  testimonial_status: "",
  growth_frustration: "",
};

function Tool({ lead }: { lead: { name: string; email: string } }) {
  const analyze = useServerFn(analyzeScan);
  const [form, setForm] = useState<FormState>(initialForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [analysis, setAnalysis] = useState<Analysis | null>(null);
  const [editedActions, setEditedActions] = useState<
    Array<{ priority: number; action: string; why: string }>
  >([]);
  const [strategicNotes, setStrategicNotes] = useState("");
  const [showReport, setShowReport] = useState(false);

  function toggle(list: "referral_sources" | "referral_activities", value: string) {
    setForm((f) => ({
      ...f,
      [list]: f[list].includes(value)
        ? f[list].filter((v) => v !== value)
        : [...f[list], value],
    }));
  }

  async function handleAnalyze(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.brand_name.trim()) {
      setError("Firm Name is required.");
      return;
    }
    setLoading(true);
    try {
      const sourcesList = [
        ...form.referral_sources,
        form.referral_sources_other && `Other: ${form.referral_sources_other}`,
      ]
        .filter(Boolean)
        .join(", ");
      const activitiesList = [
        ...form.referral_activities,
        form.referral_activities_other &&
          `Other: ${form.referral_activities_other}`,
      ]
        .filter(Boolean)
        .join(", ");

      const result = await analyze({
        data: {
          name: lead.name,
          email: lead.email,
          brand_name: form.brand_name,
          url: form.url,
          social_url: form.social_url,
          warm_intros: form.warm_intros,
          referral_sources: sourcesList,
          referral_activities: activitiesList,
          referral_outreach: form.referral_outreach,
          self_description: form.self_description,
          testimonial_status: form.testimonial_status,
          growth_frustration: form.growth_frustration,
        },
      });
      const parsed = JSON.parse(result.analysis_json) as Analysis;
      setAnalysis(parsed);
      setEditedActions(parsed.immediate_actions ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Analysis failed. Try again.");
    } finally {
      setLoading(false);
    }
  }

  if (analysis) {
    return (
      <>
        <Results
          firmName={form.brand_name}
          analysis={analysis}
          editedActions={editedActions}
          setEditedActions={setEditedActions}
          strategicNotes={strategicNotes}
          setStrategicNotes={setStrategicNotes}
          onGenerate={() => setShowReport(true)}
          onReset={() => {
            setAnalysis(null);
            setForm(initialForm);
            setEditedActions([]);
            setStrategicNotes("");
          }}
        />
        {showReport && (
          <ReportModal
            firmName={form.brand_name}
            analysis={analysis}
            editedActions={editedActions}
            strategicNotes={strategicNotes}
            onClose={() => setShowReport(false)}
          />
        )}
      </>
    );
  }

  return (
    <div>
      <p className="text-xs uppercase tracking-[0.24em] text-primary mb-3">
        Advisor Visibility Scan
      </p>
      <h1 className="text-3xl md:text-4xl font-serif tracking-tight">
        Advisor Visibility Scan Score
      </h1>
      <p className="mt-3 text-foreground/70">
        Enter a firm name and get a scored analysis of clarity, positioning, and
        referral visibility.
      </p>

      <form onSubmit={handleAnalyze} className="mt-10 space-y-8">
        <Section title="Firm Details">
          <Field label="Firm Name" required>
            <input
              type="text"
              required
              value={form.brand_name}
              onChange={(e) => setForm({ ...form, brand_name: e.target.value })}
              className="w-full rounded-lg border border-border bg-background px-4 py-2.5 focus:border-primary focus:outline-none"
            />
          </Field>
          <Field label="Website URL">
            <input
              type="url"
              placeholder="https://example.com"
              value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
              className="w-full rounded-lg border border-border bg-background px-4 py-2.5 focus:border-primary focus:outline-none"
            />
          </Field>
          <Field label="LinkedIn or Social URL">
            <input
              type="url"
              value={form.social_url}
              onChange={(e) => setForm({ ...form, social_url: e.target.value })}
              className="w-full rounded-lg border border-border bg-background px-4 py-2.5 focus:border-primary focus:outline-none"
            />
          </Field>
        </Section>

        <Section title="Intake Questions">
          <Field label="How many warm introductions does the firm receive per month or quarter?">
            <input
              type="text"
              value={form.warm_intros}
              onChange={(e) => setForm({ ...form, warm_intros: e.target.value })}
              className="w-full rounded-lg border border-border bg-background px-4 py-2.5 focus:border-primary focus:outline-none"
            />
          </Field>

          <Field label="Who are their current referral sources?">
            <CheckboxGroup
              options={REFERRAL_SOURCES}
              selected={form.referral_sources}
              onToggle={(v) => toggle("referral_sources", v)}
            />
            <input
              type="text"
              placeholder="Other"
              value={form.referral_sources_other}
              onChange={(e) =>
                setForm({ ...form, referral_sources_other: e.target.value })
              }
              className="mt-3 w-full rounded-lg border border-border bg-background px-4 py-2.5 focus:border-primary focus:outline-none"
            />
          </Field>

          <Field label="How do they currently get in front of referral sources?">
            <CheckboxGroup
              options={REFERRAL_ACTIVITIES}
              selected={form.referral_activities}
              onToggle={(v) => toggle("referral_activities", v)}
            />
            <input
              type="text"
              placeholder="Other"
              value={form.referral_activities_other}
              onChange={(e) =>
                setForm({ ...form, referral_activities_other: e.target.value })
              }
              className="mt-3 w-full rounded-lg border border-border bg-background px-4 py-2.5 focus:border-primary focus:outline-none"
            />
          </Field>

          <Field label="What does their current outreach to referral sources look like?">
            <textarea
              rows={4}
              value={form.referral_outreach}
              onChange={(e) =>
                setForm({ ...form, referral_outreach: e.target.value })
              }
              className="w-full rounded-lg border border-border bg-background px-4 py-2.5 focus:border-primary focus:outline-none"
            />
          </Field>

          <Field label="How do they describe their firm when someone could refer them?">
            <textarea
              rows={4}
              value={form.self_description}
              onChange={(e) =>
                setForm({ ...form, self_description: e.target.value })
              }
              className="w-full rounded-lg border border-border bg-background px-4 py-2.5 focus:border-primary focus:outline-none"
            />
          </Field>

          <Field label="Do they have client testimonials or experience reflections from clients?">
            <select
              value={form.testimonial_status}
              onChange={(e) =>
                setForm({ ...form, testimonial_status: e.target.value })
              }
              className="w-full rounded-lg border border-border bg-background px-4 py-2.5 focus:border-primary focus:outline-none"
            >
              <option value="">Select one…</option>
              <option>Yes, compliance-approved and in use</option>
              <option>Yes, but not cleared through compliance yet</option>
              <option>
                No, stopped collecting under the old rules and never restarted
              </option>
              <option>No, never collected them</option>
            </select>
          </Field>

          <Field label="What is their biggest growth frustration right now?">
            <textarea
              rows={4}
              value={form.growth_frustration}
              onChange={(e) =>
                setForm({ ...form, growth_frustration: e.target.value })
              }
              className="w-full rounded-lg border border-border bg-background px-4 py-2.5 focus:border-primary focus:outline-none"
            />
          </Field>
        </Section>

        {error && (
          <p className="text-sm text-destructive whitespace-pre-wrap">{error}</p>
        )}

        <button
          type="submit"
          disabled={loading}
          className="rounded-full bg-primary text-primary-foreground px-8 py-3 text-sm font-medium hover:opacity-90 disabled:opacity-50"
        >
          {loading ? "Analyzing…" : "Analyze Firm"}
        </button>
      </form>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-6 md:p-8 space-y-5">
      <h2 className="text-xl font-serif tracking-tight">{title}</h2>
      {children}
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-medium">
        {label}
        {required && <span className="text-primary"> *</span>}
      </span>
      {children}
    </label>
  );
}

function CheckboxGroup({
  options,
  selected,
  onToggle,
}: {
  options: string[];
  selected: string[];
  onToggle: (v: string) => void;
}) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {options.map((opt) => (
        <label
          key={opt}
          className="flex items-center gap-2 text-sm cursor-pointer"
        >
          <input
            type="checkbox"
            checked={selected.includes(opt)}
            onChange={() => onToggle(opt)}
            className="h-4 w-4 rounded border-border accent-primary"
          />
          <span>{opt}</span>
        </label>
      ))}
    </div>
  );
}

function Results({
  firmName,
  analysis,
  editedActions,
  setEditedActions,
  strategicNotes,
  setStrategicNotes,
  onGenerate,
  onReset,
}: {
  firmName: string;
  analysis: Analysis;
  editedActions: Array<{ priority: number; action: string; why: string }>;
  setEditedActions: (
    a: Array<{ priority: number; action: string; why: string }>,
  ) => void;
  strategicNotes: string;
  setStrategicNotes: (v: string) => void;
  onGenerate: () => void;
  onReset: () => void;
}) {
  const total = RUBRIC.reduce(
    (sum, r) => sum + (analysis[r.key as RubricKey]?.score ?? 0),
    0,
  );

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.24em] text-primary mb-2">
            Scan Results
          </p>
          <h1 className="text-3xl md:text-4xl font-serif tracking-tight">
            {firmName} — Scan Results
          </h1>
        </div>
        <div className="text-right">
          <p className="text-xs uppercase tracking-widest text-foreground/60">
            Overall Score
          </p>
          <p className="text-5xl font-serif">
            {total}
            <span className="text-2xl text-foreground/50">/35</span>
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {RUBRIC.map((r) => {
          const item = analysis[r.key as RubricKey];
          return (
            <div
              key={r.key}
              className="rounded-2xl border border-border bg-card p-5 flex gap-4"
            >
              <div
                className={`shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-white font-serif text-xl ${scoreColor(item?.score ?? 0)}`}
              >
                {item?.score ?? "-"}
              </div>
              <div>
                <h3 className="font-serif text-lg">{r.label}</h3>
                <p className="text-sm text-foreground/70 mt-1">
                  {item?.rationale}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="rounded-2xl bg-foreground text-background p-6 md:p-8">
        <h3 className="font-serif text-2xl">Visibility Gaps</h3>
        <p className="mt-3 text-background/85 leading-relaxed">
          {analysis.visibility_gaps?.observations}
        </p>
      </div>

      <div className="rounded-2xl border border-border bg-card p-6 md:p-8">
        <h3 className="font-serif text-2xl mb-4">Immediate Actions</h3>
        <p className="text-sm text-foreground/60 mb-4">
          Edit these before generating the final report.
        </p>
        <div className="space-y-4">
          {editedActions.map((a, i) => (
            <div key={i} className="rounded-xl border border-border p-4 space-y-2">
              <div className="flex items-center gap-3">
                <span className="text-xs uppercase tracking-widest text-primary">
                  Priority {a.priority}
                </span>
              </div>
              <input
                type="text"
                value={a.action}
                onChange={(e) => {
                  const next = [...editedActions];
                  next[i] = { ...a, action: e.target.value };
                  setEditedActions(next);
                }}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm font-medium"
              />
              <textarea
                value={a.why}
                onChange={(e) => {
                  const next = [...editedActions];
                  next[i] = { ...a, why: e.target.value };
                  setEditedActions(next);
                }}
                rows={2}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              />
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-6 md:p-8">
        <h3 className="font-serif text-2xl">Strategic Notes</h3>
        <p className="text-sm text-foreground/60 mt-1">
          Your own observations. Not sent to the AI — included only in the final
          report.
        </p>
        <textarea
          value={strategicNotes}
          onChange={(e) => setStrategicNotes(e.target.value)}
          rows={5}
          className="mt-4 w-full rounded-lg border border-border bg-background px-4 py-2.5"
        />
      </div>

      <div className="flex gap-3 flex-wrap">
        <button
          onClick={onGenerate}
          className="rounded-full bg-primary text-primary-foreground px-8 py-3 text-sm font-medium hover:opacity-90"
        >
          Generate Report
        </button>
        <button
          onClick={onReset}
          className="rounded-full border border-border px-8 py-3 text-sm font-medium hover:bg-muted"
        >
          Start New Scan
        </button>
      </div>
    </div>
  );
}

function ReportModal({
  firmName,
  analysis,
  editedActions,
  strategicNotes,
  onClose,
}: {
  firmName: string;
  analysis: Analysis;
  editedActions: Array<{ priority: number; action: string; why: string }>;
  strategicNotes: string;
  onClose: () => void;
}) {
  const total = RUBRIC.reduce(
    (sum, r) => sum + (analysis[r.key as RubricKey]?.score ?? 0),
    0,
  );
  const today = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  function handlePrint() {
    window.print();
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 overflow-y-auto p-4 print:static print:bg-white print:p-0">
      <div className="mx-auto max-w-3xl bg-white text-black rounded-2xl shadow-xl print:shadow-none print:rounded-none">
        <div className="flex items-center justify-between px-6 py-4 border-b print:hidden">
          <p className="text-sm font-medium">Report Preview</p>
          <div className="flex gap-2">
            <button
              onClick={handlePrint}
              className="rounded-full bg-black text-white px-4 py-2 text-xs font-medium"
            >
              Download PDF
            </button>
            <button
              onClick={handlePrint}
              className="rounded-full border border-black px-4 py-2 text-xs font-medium"
            >
              Print
            </button>
            <button
              onClick={onClose}
              className="rounded-full border border-black/30 px-4 py-2 text-xs"
            >
              Close
            </button>
          </div>
        </div>

        <div id="report-body" className="px-10 py-10 space-y-8 font-sans">
          <header className="border-b border-black/20 pb-6">
            <p className="text-[10px] uppercase tracking-[0.3em]">
              Advisor Visibility Scan
            </p>
            <h1 className="mt-2 text-3xl font-serif">{firmName}</h1>
            <p className="mt-1 text-sm text-black/60">Prepared {today}</p>
            <div className="mt-4 text-xs text-black/70 leading-relaxed">
              <p className="font-medium text-black">Katie Fernands</p>
              <p>Fractional VP Marketing | Financial Services & Fintech</p>
              <p>
                katie@katiefernands.com · katiefernands.com · 612-384-1217
              </p>
            </div>
          </header>

          <section className="rounded-xl bg-black text-white p-6 flex items-center justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] opacity-70">
                Overall Score
              </p>
              <p className="text-4xl font-serif mt-1">
                {total}
                <span className="text-xl opacity-60">/35</span>
              </p>
            </div>
            <p className="text-xs opacity-70 max-w-xs text-right">
              Sum of seven visibility criteria, scored 1–5.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-serif border-b border-black/20 pb-2 mb-3">
              Rubric
            </h2>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-widest text-black/60">
                  <th className="py-2 pr-4">Criterion</th>
                  <th className="py-2 pr-4 w-16">Score</th>
                  <th className="py-2">Rationale</th>
                </tr>
              </thead>
              <tbody>
                {RUBRIC.map((r) => {
                  const item = analysis[r.key as RubricKey];
                  return (
                    <tr key={r.key} className="border-t border-black/10 align-top">
                      <td className="py-3 pr-4 font-medium">{r.label}</td>
                      <td className="py-3 pr-4">{item?.score}/5</td>
                      <td className="py-3">{item?.rationale}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>

          <section>
            <h2 className="text-xl font-serif border-b border-black/20 pb-2 mb-3">
              Visibility Gaps
            </h2>
            <p className="text-sm leading-relaxed">
              {analysis.visibility_gaps?.observations}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-serif border-b border-black/20 pb-2 mb-3">
              Immediate Actions
            </h2>
            <ol className="space-y-4">
              {editedActions.map((a, i) => (
                <li key={i} className="text-sm">
                  <p className="font-medium">
                    {a.priority}. {a.action}
                  </p>
                  <p className="mt-1 text-black/70">{a.why}</p>
                </li>
              ))}
            </ol>
          </section>

          {strategicNotes.trim() && (
            <section>
              <h2 className="text-xl font-serif border-b border-black/20 pb-2 mb-3">
                Strategic Notes
              </h2>
              <p className="text-sm leading-relaxed whitespace-pre-wrap">
                {strategicNotes}
              </p>
            </section>
          )}

          <footer className="border-t border-black/20 pt-4 text-[10px] uppercase tracking-widest text-black/50">
            Prepared by Katie Fernands | Advisor Visibility Scan | Confidential
          </footer>
        </div>
      </div>

      <style>{`
        @media print {
          body * { visibility: hidden; }
          #report-body, #report-body * { visibility: visible; }
          #report-body { position: absolute; left: 0; top: 0; width: 100%; }
        }
      `}</style>
    </div>
  );
}
